/* eslint-disable key-spacing */

import {
  any, arrayOf, bool, func, number, object, objectOf, oneOf, oneOfType, shape, string, instanceOf,
} from 'prop-types';
import {imageShape as v1ImageShape} from '../../lib/commonTypes';

import {reactComponent} from '../commonTypes';

// Data shapes //////////////////////////////////////////////////////////////

export const appContextShape = shape({
  getAvailableTargets: func,
  getFeatureFlag: func,
  getStations: func,
  hasUserPermissionSet: func,
  setFeatureFlag: func,
});

export const moduleParams = {
  componentTransform: func,
  hideHeader: bool,
  id: string,
  initializeModule: func,
  module: oneOfType([reactComponent, object]),
  moduleButtons: arrayOf(shape({
    buttonType: string.isRequired, //oneOf(['simple', 'card', 'nav', 'action']) // Others are allowed but not suppported
    checkVisible: func,
    className: string,
    href: string,
    id: string,
    location: string, //oneOf(['HomePage', 'Nav', 'SiteTree', 'Shows', 'StoryList']),
    text: string,
  })),
  moduleName: string.isRequired,
  navId: string,
  paramList: arrayOf(string),
  permissions: arrayOf(string),
  route: oneOfType([string, instanceOf(RegExp)]),
  showNav: bool,
  title: string,
  view: func, // Backbone view override
};

export const moduleShape = shape(moduleParams);

export const moduleApi = {
  appContext: appContextShape,
  components: arrayOf(moduleShape),
  hideHeader: bool,
  navId: string,
  params: arrayOf(string),
  queryParams: objectOf(string),
  sdaPageTitle: string,
  showNav: bool,
  title: string,
  viewClass: func, // Backbone view
};

export const sourceShape = shape({
  'name': string,
  'locked': bool,
});

export const targetSiteShape = shape({
  'uuid': string,
  'created': number,
  'version': number,
  'name': string,
  'imageUrl': string,
  'url': string,
  'stagingUrl': string,
  'callLetters': string,
  'status': string,
  'city': string,
  'state': string,
  'zipcode': string,
  'weatherZipCodes': any,
  'timezone': string,
  'includedSources': arrayOf(sourceShape),
  'excludedSources': arrayOf(sourceShape),
  'savedDate': any,
  'scheduledDate': any,
  'expiresDate': any,
  'publishAuthorizationDate': any,
  'unpublishAuthorizationDate': any,
  'siteSlug': string,
  'siteType': any,
  'brandUuid': string,
  'brandName': string,
  'channel': string,
  'network': string,
  'brandColorOne': string,
  'brandColorTwo': string,
  'backgroundColorOne': string,
  'backgroundColorTwo': string,
  'textColorOne': string,
  'textColorTwo': string,
  'textColorThree': string,
  'textColorFour': string,
  'headerLogoImageUrl': string,
  'footerLogoImageUrl': string,
  'galleryLogoImageUrl': string,
  'faviconImageUrl': string,
  'weatherImageOneUrl': string,
  'weatherImageTwoUrl': string,
  'fontUrl': any,
  'designatedMarketArea': string,
  'dfpNetworkId': string,
  'dfpAdUnitId': string,
  'googleAnalyticsId': string,
  'general': shape({
    'footerText': string,
  }),
  'theme': any,
  'integration': object,
  'sources': any,
  'social': object,
  'packageTypes': arrayOf(string),
  'affiliate': any,
});

const targetSectionShape = shape({
  'uuid': string,
  'name': string,
  'navigationPath': string,
  'fullyQualifiedPath': string,
  'subsections': any,
  'tags': any,
  'parentUuid': any,
  'leaf': any,
  'order': any,
  'locked': any,
});

const attributeShape = shape({
  'uuid': string,
  'created': number,
  'name': string,
  'value': string,
  'parent': any,
});

const imageShape = shape({
  'type':            string,
  'uuid':            string,
  'title':           string,
  'created':         number,
  'version':         number,
  'attributes':      arrayOf(attributeShape),
  'caption':         string,
  'externalId':      string,
  'alt':             string,
  'original':        string,
  'metadata':        string,
  'orderNumber':     number,
  'brandUuid':       string,
  'originalUrl':     string,
  'height':          number,
  'width':           number,
  'large16x9Url':    string,
  'medium16x9Url':   string,
  'small16x9Url':    string,
  'large36x25Url':   string,
  'medium36x25Url':  string,
  'small36x25Url':   string,
  'large1x1Url':     string,
  'medium1x1Url':    string,
  'small1x1Url':     string,
  'largeScaleUrl':   string,
  'smallScaleUrl':   string,
  'originalBlurUrl': string,
  'largeBlurUrl':    string,
  'smallBlurUrl':    string,
  'isGif':           bool,
  'jumbo16x9Url':    string,
  'jumbo36x25Url':   string,
  'large3x4Url':     string,
});

const videoShape = shape({
  'type': string,
  'uuid': string,
  'title': string,
  'created': number,
  'version': number,
  'attributes': arrayOf(attributeShape),
  'caption': string,
  'externalId': string,
  'alt': string,
  'original': string,
  'orderNumber': number,
  'brandUuid': string,
  'originalUrl': string,
  'thumbUrl': string,
  'url': string,
  'videoStatus': string,
  'mp4Url': string,
});

const audioShape = shape({
  'type': string,
  'uuid': string,
  'title': string,
  'created': number,
  'version': number,
  'attributes': arrayOf(attributeShape),
  'caption': string,
  'externalId': string,
  'alt': string,
  'original': string,
  'orderNumber': number,
  'brandUuid': string,
  'originalUrl': string,
  'thumbUrl': string,
  'url': string,
  'audioStatus': string,
  'mp4Url': string,
});

export const tagShape = shape({
  'uuid': string,
  'name': string,
  'dimension': string,
  'removable': bool,
  'weight': number,
  'userGenerated': any,
  'order': any,
});

export const storyPackageShape = shape({
  'uuid': string,
  'version': number,
  'headline': string,
  'summary': string,
  'status': string,
  'versionStatus': string,
  'primaryTarget':  shape({
    'site': targetSiteShape,
    'sections': arrayOf(targetSectionShape),
  }),
  'publishedDateTime': number,
  'scheduledDateTime': any,
  'expiresDateTime': any,
  'terminatedDateTime': any,
  'savedDateTime': number,
  'publishAuthorizationDateTime': any,
  'unpublishAuthorizationDateTime': any,
  'publishedDateISO8601': string,
  'scheduledDateISO8601': string,
  'expiresDateISO8601': string,
  'terminatedDateISO8601': string,
  'savedDateTimeISO8601': string,
  'publishedDateTimeLocal': number,
  'scheduledDateTimeLocal': any,
  'expiresDateTimeLocal': any,
  'terminatedDateTimeLocal': any,
  'savedDateTimeLocal': number,
  'validPackageTypes': arrayOf(string),
  'canonicalUrl': string,
  'canonicalUrlOverride': any,
  'pageTitle': string,
  'dateLine': string,
  'byLine': string,
  'suggestedPriority': any,
  'topStoryPriority': any,
  'images': arrayOf(imageShape),
  'videos': arrayOf(videoShape),
  'audios': arrayOf(audioShape),
  'sinclairOwned': any,
  'sourceTag': any,
  'marketTag': any,
  'tags': arrayOf(tagShape),
  'userTags': arrayOf(tagShape),
  'alerts': any,
  'heroImage': shape({
    'tags': arrayOf(tagShape),
    'image': imageShape,
  }),
  'teaserImage': shape({
    'tags': arrayOf(tagShape),
    'image': imageShape,
  }),
  'filter': any,
  'domainUrl': any,
  'sectionPath': any,
  'type': string,
  'galleryAllowed': bool,
  'teaserPlacementModified': bool,
  'leadStoryPlacementModified': bool,
  'sponsored': bool,
  'brandUuid': string,
  'syndication': any,
  'richText': string,
});

export const storyPackageConfig = shape({
});

export const contentPackageShape = {
  content: object,
  contentType: oneOf(['OTTEpisode', 'OTTGroup', 'OTTSeason', 'OTTShow']).isRequired,
  version: number.isRequired,
  versionSetUuid: string.isRequired,
  versionState: oneOf(['DRAFT', 'PUBLISHED', 'UNPUBLISHED']),
  versionStateVersion: number,
};

export const episodeTeaserShape = {
  attributes: shape({
    airtimeRestrictions: arrayOf(string),
    durationSec: number.isRequired,
    lastPublishDate: string,
    lastSavedDate: string,
    seasonFormat: object,
    siteName: string,
    tagMap: arrayOf(string),
  }),
  contentTypes: arrayOf(string),
  contentVersionSetUuid: string.isRequired,
  hidden: bool,
  imageHero: shape(v1ImageShape),
  imageTeaser: shape(v1ImageShape),
  lastModified: string,
  parents: objectOf(shape(contentPackageShape)).isRequired,
  slug: string,
  summary: string,
  title: string.isRequired,
  versionStatus: arrayOf(string).isRequired,
};

export const seasonTeaserShape = {
  attributes: shape({
    airtimeRestrictions: arrayOf(string),
    durationSec: number,
    lastPublishDate: string,
    lastSavedDate: string,
    seasonFormat: object,
    siteName: string,
    tagMap: arrayOf(string),
  }),
  children: objectOf(arrayOf(shape(contentPackageShape))).isRequired,
  contentTypes: arrayOf(string),
  contentVersionSetUuid: string.isRequired,
  imageHero: shape(v1ImageShape),
  imageTeaser: shape(v1ImageShape),
  parents: objectOf(shape(contentPackageShape)).isRequired,
  slug: string,
  summary: string,
  title: string.isRequired,
  versionStatus: arrayOf(string).isRequired,
};

export const showTeaserShape = {
  attributes: shape({
    airtimeRestrictions: arrayOf(string),
    durationSec: number,
    lastPublishDate: string,
    lastSavedDate: string,
    seasonFormat: object,
    siteName: string.isRequired,
    tagMap: arrayOf(string),
  }),
  children: objectOf(arrayOf(shape(contentPackageShape))).isRequired,
  contentTypes: arrayOf(string),
  contentVersionSetUuid: string.isRequired,
  imageHero: shape(v1ImageShape),
  imageTeaser: shape(v1ImageShape),
  parents: objectOf(shape(contentPackageShape)).isRequired,
  slug: string,
  summary: string,
  title: string.isRequired,
  versionStatus: arrayOf(string).isRequired,
};


// Component Types /////////////////////////////////////////////////////////////

export const editorProperties = {
};
